import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AdjustmentsHorizontalIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  SignalIcon,
  Squares2X2Icon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import useAuthStore from '../store/authStore';
import PrintBrandHeader from './PrintBrandHeader';
import HerdContextBar from './HerdContextBar';
import AppBreadcrumbs from './AppBreadcrumbs';
import GlobalSearchLauncher from './GlobalSearchLauncher';
import FastActionDialog from './FastActionDialog';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import UserPreferenceSync from './UserPreferenceSync';
import {
  dailyNavigationConfig,
  findNavigationTrail,
  flattenNavigation,
  matchNavHref,
  navigationConfig,
  sectionShortcuts,
} from '../navigation/navConfig';
import { getHerdContextRouteConfig } from '../navigation/pageChromeConfig';
import { canAccessItem } from '../utils/permissions';
import useUiStore from '../store/uiStore';
import { systemAPI } from '../services/api';

function normalizeNavigationText(value) {
  return String(value || '')
    .replace(/[يى]/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function filterNavigationByQuery(items, query) {
  const normalized = normalizeNavigationText(query);
  if (!normalized) return items;
  return (items || []).flatMap((item) => {
    const children = item.children ? filterNavigationByQuery(item.children, query) : [];
    const matches = normalizeNavigationText(item.name).includes(normalized);
    if (!matches && !children.length) return [];
    return [{ ...item, children: item.children ? children : undefined }];
  });
}

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [navQuery, setNavQuery] = useState('');
  const [serverStatus, setServerStatus] = useState('checking');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const isAdmin = !!user?.is_admin || user?.role === 'admin';

  const setGlobalSearchOpen = useUiStore((state) => state.setGlobalSearchOpen);
  const setFastActionOpen = useUiStore((state) => state.setFastActionOpen);
  const setShortcutHelpOpen = useUiStore((state) => state.setShortcutHelpOpen);
  const batchMode = useUiStore((state) => state.batchMode);
  const setBatchMode = useUiStore((state) => state.setBatchMode);
  const sidebarCollapsed = useUiStore((state) => state.sidebarCollapsed);
  const setSidebarCollapsed = useUiStore((state) => state.setSidebarCollapsed);
  const sidebarMode = useUiStore((state) => state.sidebarMode);
  const setSidebarMode = useUiStore((state) => state.setSidebarMode);
  const headerToolsOpen = useUiStore((state) => state.headerToolsOpen);
  const setHeaderToolsOpen = useUiStore((state) => state.setHeaderToolsOpen);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = useCallback(
    (href) => matchNavHref(href, location.pathname, location.search),
    [location.pathname, location.search]
  );

  const filterItemsByAccess = useCallback((items) => items
    .map((item) => ({ ...item, children: item.children ? filterItemsByAccess(item.children) : undefined }))
    .filter((item) => {
      if (!canAccessItem(user, item)) return false;
      if (item.children && item.children.length === 0) return false;
      return true;
    }), [user]);

  const selectedNavigation = sidebarMode === 'all' ? navigationConfig : dailyNavigationConfig;
  const accessFilteredNavigation = useMemo(
    () => filterItemsByAccess(selectedNavigation),
    [filterItemsByAccess, selectedNavigation]
  );
  const filteredNav = useMemo(
    () => filterNavigationByQuery(accessFilteredNavigation, navQuery),
    [accessFilteredNavigation, navQuery]
  );

  const currentTrail = useMemo(
    () => findNavigationTrail(navigationConfig, location.pathname, location.search),
    [location.pathname, location.search]
  );
  const currentPageTitle = currentTrail[currentTrail.length - 1]?.name || 'دام‌یار';
  const herdContextRoute = useMemo(
    () => getHerdContextRouteConfig(location.pathname),
    [location.pathname]
  );
  const effectiveSidebarCollapsed = sidebarCollapsed && !sidebarOpen;

  const currentRouteIsInDailyMenu = useMemo(
    () => flattenNavigation(dailyNavigationConfig).some((item) => isActive(item.href)),
    [isActive]
  );

  const collectActiveAncestorKeys = useCallback((items, parentKey = '') => {
    const activeKeys = [];
    items.forEach((item, index) => {
      const itemKey = `${parentKey}${index}-${item.name}`;
      const childActiveKeys = item.children ? collectActiveAncestorKeys(item.children, `${itemKey}/`) : [];
      const itemIsActive = !!item.href && isActive(item.href);
      if (itemIsActive || childActiveKeys.length > 0) {
        if (item.children?.length) activeKeys.push(itemKey);
        activeKeys.push(...childActiveKeys);
      }
    });
    return activeKeys;
  }, [isActive]);

  useEffect(() => {
    let mounted = true;
    let intervalId;

    const checkServer = async () => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        if (mounted) setServerStatus('network-offline');
        return;
      }
      if (mounted) setServerStatus((current) => (current === 'online' ? current : 'checking'));
      try {
        await systemAPI.getHealth();
        if (mounted) setServerStatus('online');
      } catch (error) {
        if (mounted) setServerStatus('server-unavailable');
      }
    };

    const handleOnline = () => checkServer();
    const handleOffline = () => setServerStatus('network-offline');

    checkServer();
    intervalId = window.setInterval(checkServer, 60000);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
        setHeaderToolsOpen(false);
        return;
      }
      const targetTag = event.target?.tagName;
      const isInputLike = ['INPUT', 'TEXTAREA', 'SELECT'].includes(targetTag) || event.target?.isContentEditable;
      if (isInputLike) return;
      if (event.shiftKey && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        setFastActionOpen(true);
      } else if (event.shiftKey && event.key.toLowerCase() === 'b') {
        event.preventDefault();
        setBatchMode(!batchMode);
      } else if (event.key === '?') {
        event.preventDefault();
        setShortcutHelpOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [batchMode, setBatchMode, setFastActionOpen, setHeaderToolsOpen, setShortcutHelpOpen]);

  useEffect(() => {
    const activeKeys = collectActiveAncestorKeys(filteredNav);
    if (!activeKeys.length) return;
    setExpandedMenus((prev) => {
      const next = { ...prev };
      activeKeys.forEach((key) => { next[key] = true; });
      return next;
    });
  }, [collectActiveAncestorKeys, filteredNav]);

  useEffect(() => {
    setSidebarOpen(false);
    setHeaderToolsOpen(false);
  }, [location.pathname, location.search, setHeaderToolsOpen]);

  const itemHasActiveChild = (item) => {
    if (!item.children?.length) return false;
    return item.children.some((child) => (child.href && isActive(child.href)) || itemHasActiveChild(child));
  };

  const toggleMenu = (itemKey) => {
    setExpandedMenus((prev) => ({ ...prev, [itemKey]: !prev[itemKey] }));
  };

  const renderNavigation = (items, level = 0, parentKey = '') => items.map((item, index) => {
    const itemKey = `${parentKey}${index}-${item.name}`;
    const isOpen = navQuery ? true : !!expandedMenus[itemKey];
    const isLeafActive = item.href ? isActive(item.href) : false;
    const hasActiveChild = itemHasActiveChild(item);
    const paddingClass = level === 0 ? '' : level === 1 ? 'mr-4' : 'mr-7';
    const wrapperClass = level > 0 ? 'border-r border-slate-100 pr-2' : '';

    if (item.children?.length) {
      if (effectiveSidebarCollapsed && level === 0) {
        return (
          <button
            key={itemKey}
            type="button"
            title={item.name}
            aria-label={item.name}
            onClick={() => {
              setSidebarCollapsed(false);
              setExpandedMenus((prev) => ({ ...prev, [itemKey]: true }));
            }}
            className={`mx-auto flex h-11 w-11 items-center justify-center rounded-xl transition ${hasActiveChild ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            {item.icon ? <item.icon className="h-5 w-5" /> : <Squares2X2Icon className="h-5 w-5" />}
          </button>
        );
      }

      return (
        <div key={itemKey} className={`${paddingClass} ${wrapperClass}`.trim()}>
          <button
            type="button"
            onClick={() => toggleMenu(itemKey)}
            className={`flex min-h-10 w-full items-center justify-between rounded-lg px-3 py-2 transition-colors ${hasActiveChild ? 'bg-primary-50 text-primary-700' : 'text-slate-700 hover:bg-slate-100'}`}
            aria-expanded={isOpen}
          >
            <div className="flex min-w-0 items-center gap-2.5">
              {item.icon ? <item.icon className="h-5 w-5 shrink-0" /> : null}
              <span className={`truncate ${level === 0 ? 'text-sm font-bold' : 'text-[13px] font-semibold'}`}>{item.name}</span>
            </div>
            <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          {isOpen ? <div className="mt-1 space-y-1">{renderNavigation(item.children, level + 1, `${itemKey}/`)}</div> : null}
        </div>
      );
    }

    return (
      <div key={itemKey} className={`${paddingClass} ${wrapperClass}`.trim()}>
        <Link
          to={item.href}
          onClick={() => setSidebarOpen(false)}
          className={`flex min-h-9 items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] transition-colors ${isLeafActive ? 'bg-primary-50 font-bold text-primary-700' : 'text-slate-600 hover:bg-slate-100'}`}
        >
          {item.icon ? <item.icon className="h-4 w-4 shrink-0" /> : null}
          <span className="truncate">{item.name}</span>
        </Link>
      </div>
    );
  });

  const statusMeta = {
    online: {
      label: 'سرور در دسترس',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      dotClass: 'bg-emerald-500',
      icon: SignalIcon,
    },
    checking: {
      label: 'بررسی اتصال',
      className: 'border-slate-200 bg-slate-50 text-slate-600',
      dotClass: 'bg-slate-400 animate-pulse',
      icon: SignalIcon,
    },
    'network-offline': {
      label: 'شبکه قطع است',
      className: 'border-amber-200 bg-amber-50 text-amber-700',
      dotClass: 'bg-amber-500',
      icon: ExclamationTriangleIcon,
    },
    'server-unavailable': {
      label: 'سرور پاسخ نمی‌دهد',
      className: 'border-red-200 bg-red-50 text-red-700',
      dotClass: 'bg-red-500',
      icon: ExclamationTriangleIcon,
    },
  }[serverStatus];
  const StatusIcon = statusMeta.icon;

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen ? (
        <button
          type="button"
          aria-label="بستن منوی کناری"
          className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <aside className={`app-sidebar no-print fixed top-0 right-0 z-50 flex h-full flex-col border-l border-slate-200 bg-white shadow-xl transition-[width,transform] duration-300 lg:translate-x-0 ${sidebarCollapsed ? 'lg:w-[76px]' : 'lg:w-[280px]'} w-[280px] ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`}>
        <div className={`flex min-h-16 items-center border-b px-3 py-2 ${effectiveSidebarCollapsed ? 'lg:justify-center' : 'justify-between'}`}>
          <Link to="/" className="flex min-w-0 items-center gap-2.5" title="دام‌یار">
            <img src="/damyar-logo-mark.png" alt="نشان دام‌یار" className="h-10 w-10 shrink-0 object-contain" />
            {!effectiveSidebarCollapsed ? (
              <div className="min-w-0">
                <p className="truncate text-sm font-black text-slate-800">دام‌یار</p>
                <p className="truncate text-xs text-slate-500">مدیریت دامداری صنعتی</p>
              </div>
            ) : null}
          </Link>
          <button
            type="button"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="بستن منو"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {!effectiveSidebarCollapsed ? (
          <div className="border-b border-slate-100 p-3">
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1" role="group" aria-label="نوع منو">
              <button
                type="button"
                onClick={() => setSidebarMode('daily')}
                className={`rounded-md px-2 py-2 text-xs font-bold transition ${sidebarMode === 'daily' ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
              >
                کارهای روزانه
              </button>
              <button
                type="button"
                onClick={() => setSidebarMode('all')}
                className={`rounded-md px-2 py-2 text-xs font-bold transition ${sidebarMode === 'all' ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
              >
                همه امکانات
              </button>
            </div>
            <div className="relative mt-2">
              <MagnifyingGlassIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={navQuery}
                onChange={(event) => setNavQuery(event.target.value)}
                className="input py-2 pr-9 text-sm"
                placeholder="جست‌وجو در منو..."
                aria-label="جست‌وجو در منوی سایت"
              />
            </div>
            {sidebarMode === 'daily' && !currentRouteIsInDailyMenu ? (
              <button
                type="button"
                onClick={() => setSidebarMode('all')}
                className="mt-2 w-full rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-right text-xs font-semibold text-amber-800"
              >
                این صفحه در «همه امکانات» قرار دارد؛ نمایش فهرست کامل
              </button>
            ) : null}
          </div>
        ) : null}

        <nav className={`flex-1 space-y-1 overflow-y-auto ${effectiveSidebarCollapsed ? 'p-2' : 'p-3'} pb-24`} aria-label="منوی اصلی">
          {filteredNav.length ? renderNavigation(filteredNav) : (
            !effectiveSidebarCollapsed ? <p className="rounded-lg bg-slate-50 p-3 text-center text-xs text-slate-500">گزینه‌ای با این عنوان پیدا نشد.</p> : null
          )}
        </nav>

        <div className={`mt-auto border-t bg-slate-50 ${effectiveSidebarCollapsed ? 'p-2' : 'p-3'}`}>
          <div className={`flex items-center gap-2 ${effectiveSidebarCollapsed ? 'justify-center' : ''}`}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100" title={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}>
              <span className="text-sm font-black text-primary-700">{user?.first_name?.[0] || user?.username?.[0] || 'U'}</span>
            </div>
            {!effectiveSidebarCollapsed ? (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-900">{user?.first_name} {user?.last_name}</p>
                <p className="truncate text-xs text-slate-500">{user?.role_display}</p>
              </div>
            ) : null}
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
              aria-label="خروج از سامانه"
              title="خروج از سامانه"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -left-3 top-20 hidden h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:text-primary-700 lg:flex"
          aria-label={sidebarCollapsed ? 'بازکردن منوی کناری' : 'جمع‌کردن منوی کناری'}
          title={sidebarCollapsed ? 'بازکردن منو' : 'جمع‌کردن منو'}
        >
          {sidebarCollapsed ? <ChevronDoubleLeftIcon className="h-4 w-4" /> : <ChevronDoubleRightIcon className="h-4 w-4" />}
        </button>
      </aside>

      <div className={`app-content print-main-full transition-[margin] duration-300 ${sidebarCollapsed ? 'lg:mr-[76px]' : 'lg:mr-[280px]'}`}>
        <header className="app-header no-print sticky top-0 z-30 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
          <div className="flex h-14 items-center justify-between gap-3 px-4 lg:px-5">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
                onClick={() => setSidebarOpen(true)}
                aria-label="بازکردن منوی اصلی"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <div className="min-w-0">
                <p className="truncate text-sm font-black text-slate-900">{currentPageTitle}</p>
                <p className="hidden truncate text-xs text-slate-500 sm:block">{currentTrail.slice(0, -1).map((item) => item.name).join(' / ') || 'سامانه عملیاتی دام‌یار'}</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={() => setGlobalSearchOpen(true)}
                className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-sm font-bold text-slate-600 transition hover:border-primary-200 hover:text-primary-700 sm:px-3"
                aria-label="جست‌وجوی سراسری"
                title="جست‌وجوی سراسری"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
                <span className="hidden xl:inline">جست‌وجو</span>
              </button>
              <button
                type="button"
                onClick={() => setFastActionOpen(true)}
                className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-sm font-bold text-slate-600 transition hover:border-primary-200 hover:text-primary-700 sm:px-3"
                aria-label="اقدام سریع"
                title="اقدام سریع"
              >
                <WrenchScrewdriverIcon className="h-5 w-5" />
                <span className="hidden xl:inline">اقدام سریع</span>
              </button>
              <button
                type="button"
                onClick={() => setHeaderToolsOpen(!headerToolsOpen)}
                className={`inline-flex min-h-10 items-center gap-2 rounded-lg border px-2.5 text-sm font-bold transition sm:px-3 ${headerToolsOpen ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600 hover:border-primary-200 hover:text-primary-700'}`}
                aria-label="نمایش ابزارهای بالای صفحه"
                aria-expanded={headerToolsOpen}
              >
                <AdjustmentsHorizontalIcon className="h-5 w-5" />
                <span className="hidden xl:inline">ابزارها</span>
              </button>
              <span
                className={`inline-flex min-h-10 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-bold ${statusMeta.className}`}
                title={statusMeta.label}
                aria-live="polite"
              >
                <StatusIcon className="h-4 w-4" />
                <span className={`h-2 w-2 rounded-full ${statusMeta.dotClass}`} />
                <span className="hidden lg:inline">{statusMeta.label}</span>
              </span>
              <span className="hidden rounded-lg border border-primary-100 bg-primary-50 px-2.5 py-2 text-xs font-bold text-primary-700 2xl:inline-flex">
                {user?.role_display || (isAdmin ? 'مدیر سامانه' : 'کاربر سامانه')}
              </span>
            </div>
          </div>

          {headerToolsOpen ? (
            <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 lg:px-5">
              <div className="flex flex-wrap items-center gap-2">
                {sectionShortcuts.filter((item) => canAccessItem(user, item)).map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`rounded-lg border px-2.5 py-1.5 text-xs font-bold transition ${isActive(item.href) ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600 hover:border-primary-200'}`}
                  >
                    {item.name}
                  </Link>
                ))}
                <span className="mx-1 hidden h-6 w-px bg-slate-200 md:block" />
                <button
                  type="button"
                  onClick={() => setBatchMode(!batchMode)}
                  className={`rounded-lg border px-2.5 py-1.5 text-xs font-bold transition ${batchMode ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-600'}`}
                  aria-pressed={batchMode}
                >
                  {batchMode ? 'ثبت گروهی فعال' : 'حالت ثبت گروهی'}
                </button>
                <button
                  type="button"
                  onClick={() => setShortcutHelpOpen(true)}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-600"
                >
                  راهنمای میان‌برها
                </button>
                <span className="mr-auto hidden text-xs text-slate-500 xl:inline">
                  {new Date().toLocaleDateString('fa-IR-u-ca-persian', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>
            </div>
          ) : null}

          <div className="border-t border-slate-100 px-4 py-1.5 lg:px-5">
            <AppBreadcrumbs />
          </div>
          {herdContextRoute ? <HerdContextBar routeConfig={herdContextRoute} /> : null}
        </header>

        <main className="app-main p-3 sm:p-4 lg:p-5">
          <PrintBrandHeader />
          {children}
        </main>
      </div>
      <GlobalSearchLauncher />
      <FastActionDialog />
      <KeyboardShortcutsHelp />
      <UserPreferenceSync />
    </div>
  );
}

export default Layout;
